import {differenceInHours, formatDistanceToNow} from 'date-fns'
import {formatInTimeZone} from 'date-fns-tz'

import {DopamineSessionSummary} from 'interfaces'
import {getBrowserTimeZone} from 'utils'

declare const dopamineAssistantStorageScopeBrand: unique symbol

const DOPAMINE_ASSISTANT_COLLAPSE_EVENT = 'dopamine-assistant:collapse'

export const requestDopamineAssistantCollapse = (): void => {
  window.dispatchEvent(new Event(DOPAMINE_ASSISTANT_COLLAPSE_EVENT))
}

export const subscribeToDopamineAssistantCollapse = (
  listener: () => void,
): (() => void) => {
  window.addEventListener(DOPAMINE_ASSISTANT_COLLAPSE_EVENT, listener)
  return () =>
    window.removeEventListener(DOPAMINE_ASSISTANT_COLLAPSE_EVENT, listener)
}

export type DopamineAssistantStorageScope = string & {
  readonly [dopamineAssistantStorageScopeBrand]: true
}

export const createDopamineAssistantStorageScope = (
  account: string,
  tenant: string,
): DopamineAssistantStorageScope | null => {
  const normalizedAccount = account.trim().toLowerCase()
  const normalizedTenant = tenant.trim().toLowerCase()

  if (!normalizedAccount || !normalizedTenant) return null

  return JSON.stringify([
    normalizedAccount,
    normalizedTenant,
  ]) as DopamineAssistantStorageScope
}

export const createDopamineAssistantSessionId = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()

  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

export const doesDopamineSessionMatchSearch = (
  session: DopamineSessionSummary,
  query: string,
): boolean => session.title.toLowerCase().includes(query.toLowerCase())

export const formatDopamineSessionCreatedAt = (date: string): string => {
  if (!date) return ''

  const timeZone = getBrowserTimeZone()
  const originalDate = new Date(date)
  const now = new Date()
  const hoursDiff = differenceInHours(now, originalDate)
  const isSameLocalDay =
    formatInTimeZone(originalDate, timeZone, 'yyyy-MM-dd') ===
    formatInTimeZone(now, timeZone, 'yyyy-MM-dd')

  if (hoursDiff < 24 && isSameLocalDay) {
    return formatDistanceToNow(originalDate, {addSuffix: true})
  }

  return `${formatInTimeZone(
    originalDate,
    timeZone,
    'dd MMM yyyy',
  )} at ${formatInTimeZone(originalDate, timeZone, 'hh:mm a')}`
}
